/**
 * Test Template Dialog
 * 
 * Allows testing email templates with sample data before activation.
 * Validates template syntax and shows rendered preview.
 */

import { useState } from 'react';
import {
  Card,
  CardContent,
  Button,
  TextArea,
  toast
} from '@etherisc/ui-kit';
import { XIcon, PlayIcon, AlertCircleIcon } from 'lucide-react';

interface TestTemplateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  templateName: string;
  templateType: string;
}

export function TestTemplateDialog({
  isOpen,
  onClose,
  templateName,
  templateType
}: TestTemplateDialogProps) {
  const [variables, setVariables] = useState('{\n  "title": "Test Email",\n  "recipientName": "John Doe",\n  "message": "This is a test message"\n}');
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');
  const [testing, setTesting] = useState(false);

  const handleTest = async () => {
    try {
      setTesting(true);
      setError('');
      setPreview('');

      // Parse variables
      let parsedVariables;
      try {
        parsedVariables = JSON.parse(variables);
      } catch {
        throw new Error('Invalid JSON in variables field');
      }

      // For EMAIL templates, render preview using backend
      // For now, just validate JSON and show success
      // TODO: Add backend endpoint for template preview
      
      setPreview(`
        <div class="preview-placeholder">
          <h3>Preview not yet implemented</h3>
          <p>Template: ${templateName}</p>
          <p>Type: ${templateType}</p>
          <p>Variables validated successfully:</p>
          <pre>${JSON.stringify(parsedVariables, null, 2)}</pre>
        </div>
      `);

      toast.success('Template variables valid', {
        description: 'Template syntax will be validated on upload'
      });

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Test failed');
      toast.error('Template test failed', {
        description: err instanceof Error ? err.message : 'Unknown error'
      });
    } finally {
      setTesting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Test Template: {templateName}</h2>
              <Button variant="outline" size="sm" onClick={onClose}>
                <XIcon size={16} />
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Variables Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Test Variables (JSON)
                </label>
                <TextArea
                  id="variables"
                  value={variables}
                  onChange={(e) => setVariables(e.target.value)}
                  rows={15}
                  placeholder='{\n  "variable": "value"\n}'
                  className="font-mono text-sm"
                />
                <p className="text-sm text-gray-500 mt-2">
                  Enter JSON object with template variables
                </p>
              </div>

              {/* Preview */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preview
                </label>
                {error ? (
                  <div className="bg-red-50 border border-red-200 rounded p-4">
                    <div className="flex items-start gap-2">
                      <AlertCircleIcon size={20} className="text-red-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-900">Error</p>
                        <p className="text-sm text-red-700 mt-1">{error}</p>
                      </div>
                    </div>
                  </div>
                ) : preview ? (
                  <div 
                    className="border rounded p-4 bg-white overflow-auto max-h-[400px]"
                    dangerouslySetInnerHTML={{ __html: preview }}
                  />
                ) : (
                  <div className="border rounded p-4 bg-gray-50 text-center text-gray-500">
                    Click "Test Template" to preview
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 mt-6">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleTest} disabled={testing}>
                <PlayIcon size={16} className="mr-2" />
                {testing ? 'Testing...' : 'Test Template'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

